/**
 * MadCrab_V Verbal API contract (port 8787).
 * Source of truth: technical/src/lib/generateRwBatch.js + digitalSatRwSpec.js
 *
 * Endpoint: POST /api/questions/fast-batch
 * Body: { category: "rw:{skill}:{difficulty}", limit: number }
 * Response: { ok: true, questions: VerbalRawQuestion[] }
 */

import type { Question, LocalizedText } from './types';

export type VerbalDifficulty = 'easy' | 'medium';

export const VERBAL_SKILL_KEYS = [
    'craft_structure',
    'information_ideas',
    'standard_english_conventions',
    'expression_ideas',
] as const;

export type VerbalSkillKey = (typeof VERBAL_SKILL_KEYS)[number];

const OPTION_LETTERS = ['A', 'B', 'C', 'D'] as const;

/** Build SQLite cache category key used by MadCrab Verbal API. */
export function makeRwCacheCategory(
    categoryKey: string,
    difficulty: VerbalDifficulty | string,
): string {
    const diff = String(difficulty ?? 'easy').trim() || 'easy';
    let key = String(categoryKey ?? '').trim();
    while (key.startsWith('rw:')) {
        key = key.slice(3);
    }
    const parts = key.split(':').filter((p) => p.length > 0);
    if (parts.length === 0) {
        return `rw::${diff}`;
    }
    return `rw:${parts[0]}:${diff}`;
}

/** Map UI difficulty labels to Verbal API easy/medium. */
export function mapUiDifficultyToVerbal(
    value: string | null | undefined,
): VerbalDifficulty {
    const v = String(value ?? '').trim().toLowerCase();
    if (v === 'advanced' || v === 'medium' || v === 'hard' || v === 't2' || v === 't3') {
        return 'medium';
    }
    return 'easy';
}

/** Extract skill key from topic query like verbal/craft_structure or craft_structure. */
export function parseVerbalSkillKey(topic: string): VerbalSkillKey | null {
    const raw = String(topic ?? '')
        .trim()
        .replace(/^verbal\//, '')
        .replace(/^rw:/, '')
        .split(':')[0];
    if (VERBAL_SKILL_KEYS.includes(raw as VerbalSkillKey)) {
        return raw as VerbalSkillKey;
    }
    // Legacy ids from earlier SPA
    const legacy: Record<string, VerbalSkillKey> = {
        craft_and_structure: 'craft_structure',
        information_and_ideas: 'information_ideas',
        expression_of_ideas: 'expression_ideas',
        standard_english_conventions: 'standard_english_conventions',
    };
    return legacy[raw] ?? null;
}

export interface VerbalRawQuestion {
    id?: string;
    question?: string;
    question_text?: string;
    passage?: string;
    options?: unknown;
    correct_answer?: string;
    correct_option_index?: number;
    explanation?: string;
    explanation_ru?: string;
    graphic?: string;
    [key: string]: unknown;
}

export interface VerbalFastBatchResponse {
    ok: boolean;
    questions: VerbalRawQuestion[];
    count?: number;
    error?: string;
    no_more_questions?: boolean;
}

function stripOptionPrefix(text: string): string {
    const s = String(text ?? '').trim();
    const m = s.match(/^([A-D])\s*[).\]:]\s*(.+)$/i);
    if (m?.[2]) return m[2].trim();
    const m2 = s.match(/^([A-D])\s+(.+)$/i);
    if (m2?.[2]) return m2[2].trim();
    return s;
}

function normalizeOptions(raw: unknown): string[] {
    if (!raw) return [];
    if (typeof raw === 'string') {
        const trimmed = raw.trim();
        if (trimmed.startsWith('[')) {
            try {
                return normalizeOptions(JSON.parse(trimmed));
            } catch {
                return trimmed ? [stripOptionPrefix(trimmed)] : [];
            }
        }
        return trimmed ? [stripOptionPrefix(trimmed)] : [];
    }
    if (Array.isArray(raw)) {
        return raw
            .map((o, idx) => {
                if (typeof o === 'string') return stripOptionPrefix(o);
                if (o && typeof o === 'object') {
                    const obj = o as Record<string, unknown>;
                    const text =
                        obj.text != null
                            ? String(obj.text)
                            : obj.value != null
                              ? String(obj.value)
                              : '';
                    return stripOptionPrefix(text) || `Option ${OPTION_LETTERS[idx] ?? idx}`;
                }
                return '';
            })
            .filter(Boolean)
            .slice(0, 4);
    }
    if (typeof raw === 'object') {
        return OPTION_LETTERS.map((letter) => {
            const v = (raw as Record<string, unknown>)[letter];
            return v == null ? '' : stripOptionPrefix(String(v));
        }).filter(Boolean);
    }
    return [];
}

function letterToIndex(letter: string | undefined, optionsLength: number): number {
    const L = String(letter ?? '')
        .trim()
        .toUpperCase()
        .charAt(0);
    const idx = OPTION_LETTERS.indexOf(L as (typeof OPTION_LETTERS)[number]);
    if (idx >= 0 && idx < optionsLength) return idx;
    return 0;
}

function stripEnPrefix(text: string): string {
    return String(text ?? '').replace(/^EN:\s*/i, '').trim();
}

function buildExplanation(
    enRaw: string | undefined,
    ruRaw: string | undefined,
): LocalizedText {
    const en = stripEnPrefix(enRaw ?? '');
    const ru = String(ruRaw ?? '').trim();
    const translations: Record<string, string> = {};
    if (en) translations.en = en;
    if (ru) translations.ru = ru;
    if (!translations.en && !translations.ru) {
        translations.en = '';
    }
    return {
        translations,
        default_language: translations.en ? 'en' : 'ru',
    };
}

/** Normalize MadCrab Verbal bank item → Math 3.0 Question shape. */
export function normalizeVerbalQuestion(raw: VerbalRawQuestion): Question {
    const options = normalizeOptions(raw.options);
    const stem =
        (raw.question_text != null && String(raw.question_text).trim()) ||
        (raw.question != null && String(raw.question).trim()) ||
        '';
    const id = String(raw.id ?? `verbal-${Math.random().toString(36).slice(2, 10)}`);
    const correctIndex =
        typeof raw.correct_option_index === 'number'
            ? raw.correct_option_index
            : letterToIndex(raw.correct_answer, options.length || 4);

    const question: Question = {
        id,
        question_id: id,
        question_text: stem,
        options: options.length > 0 ? options : ['A', 'B', 'C', 'D'],
        correct_option_index: correctIndex,
        graph_data: null,
        svg_data: raw.graphic ? String(raw.graphic) : null,
        passage: raw.passage != null ? String(raw.passage) : '',
        explanation: buildExplanation(raw.explanation, raw.explanation_ru),
        difficulty: 'easy',
        skill_id: String(raw.question_type ?? 'verbal'),
        provenance: {
            solver_version: 'madcrab-verbal',
            prompt_hash_sha256: '',
            seed_hash_sha256: '',
            pipeline_version: 'madcrab_v',
            timestamp_utc: new Date().toISOString(),
        },
        format: 'MCQ',
        render_mode: raw.graphic ? 'svg' : 'text',
    };

    return question;
}

export async function fetchVerbalFastBatch(
    categoryKey: string,
    difficulty: VerbalDifficulty,
    limit: number = 10,
): Promise<Question[]> {
    const category = makeRwCacheCategory(categoryKey, difficulty);
    const response = await fetch('/api/questions/fast-batch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category, limit }),
    });

    if (!response.ok) {
        const text = await response.text().catch(() => '');
        throw new Error(text || `Verbal API HTTP ${response.status}`);
    }

    const data = (await response.json()) as VerbalFastBatchResponse;
    if (!data || data.ok !== true || !Array.isArray(data.questions)) {
        throw new Error(data?.error || 'DATABASE_ACCESS_ERROR: Pool is empty');
    }

    const normalized = data.questions.filter(Boolean).map(normalizeVerbalQuestion);
    if (normalized.length === 0) {
        throw new Error('DATABASE_ACCESS_ERROR: Pool is empty');
    }
    return normalized;
}

/** MadCrab smart drill: GET /api/progress/weakest-skill */
export async function fetchWeakestVerbalSkill(): Promise<string | null> {
    try {
        const response = await fetch('/api/progress/weakest-skill');
        const data = (await response.json().catch(() => ({}))) as {
            ok?: boolean;
            skill_secondary?: string | null;
        };
        if (!response.ok || data.ok === false) return null;
        const skill = data.skill_secondary != null ? String(data.skill_secondary).trim() : '';
        return skill || null;
    } catch {
        return null;
    }
}
